import { TargetPercent } from "../redux/powderOrder";
import { useCallback } from "react";
import { usePowder } from "../redux/ReduxContext";

const normalize = (val: number) => {
    return Math.max(0, Math.min(100, +val.toFixed(4)));
};

const toDisplayValue = (value: number | null | undefined) => {
    if (typeof value !== "number") return "";

    return value.toString().replace(".", ",");
};

const formatInput = (rawValue: string) => {
    if (!rawValue) return "";

    let value = rawValue.replace(/\./g, ",");

    if (value.startsWith(",")) {
        value = "0" + value;
    }

    return value;
};

const toNumber = (value: string) => {
    const normalized = value.replace(",", ".");
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
};

const getNumericValue = (value: number | null | undefined) => {
    return typeof value === "number" ? value : 0;
};

export const useBalancedPercents = () => {
    const { state, dispatch } = usePowder();

    const recalc = useCallback((targetPercents: TargetPercent[], balanceElement: string | null) => {
        if (!balanceElement) return targetPercents;

        let sum = 0;

        targetPercents.forEach(t => {
            if (t.symbol !== balanceElement) {
                sum += typeof t.value === "number" ? t.value : 0;
            }
        });

        const index = targetPercents.findIndex(t => t.symbol === balanceElement);

        if (index !== -1) {
            const nextValue = normalize(100 - sum);

            targetPercents[index] = {
                ...targetPercents[index],
                value: nextValue,
                displayValue: toDisplayValue(nextValue)
            };
        }

        return [...targetPercents];
    }, []);

    const updateValue = useCallback((symbol: string, rawValue: string) => {
        if (!/^[0-9]*[.,]?[0-9]*$/.test(rawValue)) return;

        const formatted = formatInput(rawValue);
        const numericValue = toNumber(formatted);
        const isEmptyInput = formatted === "";

        if (!state.targetPercents) return;

        let nextValue = isEmptyInput ? 0 : numericValue;
        let nextDisplayValue = formatted;

        if (
            nextValue !== null
            && state.balanceElement
            && symbol !== state.balanceElement
        ) {
            const otherSum = state.targetPercents.reduce((sum, target) => {
                if (target.symbol === symbol || target.symbol === state.balanceElement) {
                    return sum;
                }

                return sum + getNumericValue(target.value);
            }, 0);

            const maxAllowed = normalize(Math.max(0, 100 - otherSum));
            const requestedValue = nextValue;
            const clampedValue = normalize(Math.min(requestedValue, maxAllowed));

            nextValue = clampedValue;

            if (!isEmptyInput && clampedValue !== requestedValue) {
                nextDisplayValue = toDisplayValue(clampedValue);
            }
        }

        const updated = state.targetPercents.map(t =>
            t.symbol === symbol
                ? {
                    ...t,
                    value: nextValue,
                    displayValue: nextDisplayValue
                }
                : t
        );

        const final =
            nextValue !== null
                ? recalc(updated, state.balanceElement)
                : updated;

        dispatch({
            type: "SET_FIELD",
            payload: {
                field: "targetPercents",
                value: final
            }
        });

    }, [state.targetPercents, state.balanceElement, dispatch, recalc]);

    const setBalance = useCallback((symbol: string | null) => {
        const final = recalc([...(state.targetPercents ?? [])], symbol);

        dispatch({
            type: "SET_FIELD",
            payload: {
                field: "balanceElement",
                value: symbol
            }
        });

        dispatch({
            type: "SET_FIELD",
            payload: {
                field: "targetPercents",
                value: final
            }
        });
    }, [state.targetPercents, dispatch, recalc]);

    return {
        updateValue,
        setBalance
    };
};